import { Field, Message, RadioGroup } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection } from 'Components/Layout';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { getNumberOfSeparations, isPhaseEarlierThan } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Helpers';
import { Phase } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';

let SpecimenPreparationSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const dryingPhase = useSelector(createWorksheetSelector!('Preparation.DryingPhase')) as Phase;
  const washingPhase = useSelector(createWorksheetSelector!('Preparation.WashingPhase')) as Phase;

  const numberOfSeparations = useSelector(pipe(createWorksheetSelector!('', false), getNumberOfSeparations));

  const materialDriedOptions: Array<{ label: string; value: Phase; disabled?: boolean }> = [
    { label: 'None (assumed dry)', value: Phase.Never },
    { label: 'Whole specimen', value: Phase.WholePortion },
    {
      label: 'After first separation (coarser assumed dry)',
      value: Phase.AfterFirstSeparation,
      disabled: numberOfSeparations < 1,
    },
    {
      label: 'After second separation (coarser assumed dry)',
      value: Phase.AfterSecondSeparation,
      disabled: numberOfSeparations < 2,
    },
  ];

  const materialWashedOptions: Array<{ label: string; value: Phase; disabled?: boolean }> = [
    { label: 'None', value: Phase.Never },
    {
      label: 'Whole specimen',
      value: Phase.WholePortion,
      disabled: isPhaseEarlierThan(Phase.WholePortion, dryingPhase),
    },
    {
      label: 'After first separation',
      value: Phase.AfterFirstSeparation,
      disabled: numberOfSeparations < 1 || isPhaseEarlierThan(Phase.AfterFirstSeparation, dryingPhase),
    },
    {
      label: 'After second separation',
      value: Phase.AfterSecondSeparation,
      disabled: numberOfSeparations < 2 || isPhaseEarlierThan(Phase.AfterSecondSeparation, dryingPhase),
    },
  ];

  return (
    <NamePrefixModifier appendNamePrefix="Preparation">
      <ListFieldSection>
        <Field
          input={<RadioGroup />}
          label="Material Dried"
          name="DryingPhase"
          options={materialDriedOptions}
          confirm={dryingPhase !== Phase.Never}
          confirmTitle="Confirm Drying Selection"
          confirmDialogWidth="550px"
          confirmText={(newDryingPhase: Phase) => (
            <>
              <div>
                <Message border={false} info>
                  {`Previous drying selection: ${materialDriedOptions.find((o) => o.value === dryingPhase)?.label}`}
                </Message>
              </div>
              <div>
                <Message border={false} info>
                  {`Previous washing selection: ${materialWashedOptions.find((o) => o.value === washingPhase)?.label}`}
                </Message>
              </div>
              <br />
              <div>
                <Message border={false} info>
                  {`New drying selection: ${materialDriedOptions.find((o) => o.value === newDryingPhase)?.label}`}
                </Message>
              </div>
              <br />
              <div>
                <Message border={false} error>
                  Changing the drying selection will clear data entered for dry mass measurements.
                </Message>
              </div>
              <br />
              {isPhaseEarlierThan(washingPhase, newDryingPhase) ? (
                <>
                  <div>
                    <Message border={false} error>
                      Changing the drying selection will make the previous washing selection invalid and clear data
                      entered for the previous washing.
                    </Message>
                  </div>
                  <br />
                </>
              ) : null}
              <div>Continue?</div>
            </>
          )}
        />
        <Field
          input={<RadioGroup />}
          label="Material Washed"
          name="WashingPhase"
          options={materialWashedOptions}
          confirm={washingPhase !== Phase.Never}
          confirmTitle="Confirm Washing Selection"
          confirmDialogWidth="550px"
          confirmText={(newWashingPhase: Phase) => (
            <>
              <div>
                <Message border={false} info>
                  {`Previous washing selection: ${materialWashedOptions.find((o) => o.value === washingPhase)?.label}`}
                </Message>
              </div>
              <br />
              <div>
                <Message border={false} info>
                  {`New washing selection: ${materialWashedOptions.find((o) => o.value === newWashingPhase)?.label}`}
                </Message>
              </div>
              <br />
              <div>
                <Message border={false} error>
                  {'Changing the washing selection will clear data entered for the previous washing.'}
                </Message>
              </div>
              <br />
              <div>Continue?</div>
            </>
          )}
        />
      </ListFieldSection>
    </NamePrefixModifier>
  );
};

SpecimenPreparationSection = withInjectedProps(SpecimenPreparationSection);

export { SpecimenPreparationSection };
export default SpecimenPreparationSection;
